/**
 * features/terms/TermsList.tsx
 * ------------------------------------------------------------------
 * Round 7 — unified Terms entry point. Lists all three terms for
 * the current academic year with a status pill, a deadline summary,
 * and a click-through to the per-term detail page.
 *
 * Replaces the side-by-side three-column layout of the old
 * TermManagement screen. The detail page (TermDetail.tsx) holds
 * five tabs combining the old TermManagement and Assessment
 * Configuration screens into one cohesive surface.
 *
 * Reached at #/terms (admin only; gated by canManageTerms).
 * Each row links to #/terms/Term1 etc.
 * ------------------------------------------------------------------ */
import { useMemo } from 'react';
import {
  Lock,
  Unlock,
  ShieldAlert,
  CalendarDays,
  ChevronRight,
  AlertTriangle,
  CalendarClock,
} from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { SCHOOL, TermKey, termDates } from '@/lib/school.config';
import { getCaLabels } from '@/lib/assessmentConfig';
import { Card, PageHeader } from '@/components/ui/primitives';
import type { TermState, TermStatusDocument } from '@/types';

interface Props {
  year: string;
}

const STATUS_META: Record<
  TermState,
  { label: string; icon: typeof Lock; cls: string }
> = {
  open: {
    label: 'Open',
    icon: Unlock,
    cls: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  },
  closed: {
    label: 'Closed',
    icon: Lock,
    cls: 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  },
  locked: {
    label: 'Locked',
    icon: ShieldAlert,
    cls: 'text-red-700 bg-red-50 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  },
};

/** Fallback used when a TermStatusDocument.status holds an unexpected
 *  value (e.g. demo seed wrote 'not_started'). Keeps the row visible
 *  with an accurate "unknown" pill instead of crashing the page. */
const UNKNOWN_STATUS_META = {
  label: 'Unknown',
  icon: AlertTriangle,
  cls: 'text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};

export default function TermsList({ year }: Props) {
  const { termStatuses, assessmentConfigs } = useData();

  /** Resolve the TermStatusDocument for a (year, term) — undefined if unset. */
  const statusFor = (term: TermKey): TermStatusDocument | undefined =>
    termStatuses.find((t) => t.year === year && t.term === term);

  /** Pretty short date for the deadline summary, e.g. "15 Apr". */
  function shortDate(iso: string): string {
    try {
      const d = new Date(iso + 'T12:00:00Z');
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch {
      return iso;
    }
  }

  /** Build the brief "Deadlines: …" line. Returns null if nothing's set. */
  function deadlineSummary(term: TermKey): {
    text: string;
    overdue: boolean;
  } | null {
    const s = statusFor(term);
    if (!s?.deadlines) return null;
    const todayIso = new Date().toISOString().slice(0, 10);
    const items: string[] = [];
    let overdue = false;
    const ca = s.deadlines.ca ?? {};
    for (const [label, date] of Object.entries(ca)) {
      if (!date) continue;
      items.push(`${label} ${shortDate(date)}`);
      if (date < todayIso) overdue = true;
    }
    if (s.deadlines.termDeadline) {
      items.push(`Final ${shortDate(s.deadlines.termDeadline)}`);
      if (s.deadlines.termDeadline < todayIso) overdue = true;
    }
    if (items.length === 0) return null;
    return { text: items.join(' · '), overdue };
  }

  const rows = useMemo(() => {
    return (Object.keys(SCHOOL.terms) as TermKey[]).map((term) => {
      const s = statusFor(term);
      const status: TermState = s?.status ?? 'open';
      const monthLocks = s?.monthLocks ?? {};
      const lockedMonths = Object.entries(monthLocks).filter(([, v]) => v).map(([k]) => k);
      const caCount = getCaLabels(term, assessmentConfigs).length;
      const summary = deadlineSummary(term);
      return { term, status, lockedMonths, caCount, summary };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termStatuses, assessmentConfigs, year]);

  return (
    <div>
      <PageHeader
        title="Terms"
        subtitle={`Manage academic-year ${year}: status, deadlines, assessments, exam titles and report notices`}
      />

      <div className="space-y-3">
        {rows.map(({ term, status, lockedMonths, caCount, summary }) => {
          // Be resilient to unexpected status values written by older
          // seeds or external migrations (e.g. 'not_started').
          const meta = STATUS_META[status] ?? UNKNOWN_STATUS_META;
          const StatusIcon = meta.icon;
          return (
            <a
              key={term}
              href={`#/terms/${term}`}
              className="block group"
            >
              <Card className="p-5 transition-all hover:border-primary hover:shadow-md cursor-pointer">
                <div className="flex items-center gap-4">
                  {/* Term name + dates */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-bold text-base text-gray-800 dark:text-white">
                        {SCHOOL.terms[term].label}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}
                      >
                        <StatusIcon size={11} />
                        {meta.label}
                      </span>
                      {lockedMonths.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
                          <Lock size={10} />
                          {lockedMonths.length} month{lockedMonths.length === 1 ? '' : 's'} locked
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock size={11} />
                        {termDates(term)}
                      </span>
                      <span>·</span>
                      <span>
                        <strong className="text-gray-700 dark:text-gray-300">{caCount}</strong>{' '}
                        CA{caCount === 1 ? '' : 's'}
                      </span>
                      {summary && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays size={11} />
                            <span className={summary.overdue ? 'text-amber-700 dark:text-amber-400' : ''}>
                              {summary.text}
                            </span>
                            {summary.overdue && (
                              <AlertTriangle size={11} className="text-amber-600" />
                            )}
                          </span>
                        </>
                      )}
                      {!summary && (
                        <>
                          <span>·</span>
                          <span className="text-gray-400 italic">No deadlines set</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <ChevronRight
                    size={18}
                    className="text-gray-400 shrink-0 group-hover:text-primary group-hover:translate-x-0.5 transition-all"
                  />
                </div>
              </Card>
            </a>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-gray-400 text-center">
        Click any term to manage its status, deadlines, CA labels, exam titles and report notices.
      </p>
    </div>
  );
}
